import { useCallback, useEffect, useState } from 'react';
import type { Role } from '@domain/entities';
import type { RoleFilters, RolePayload } from '@application/dto';
import { getListRolesUseCase, getSaveRoleUseCase, getToggleRoleStatusUseCase } from '@di';
import { describeError } from '@shared/utils';

export function useRoles(filters: Omit<RoleFilters, 'page' | 'limit'>, pageSize = 50) {
  const [roles, setRoles] = useState<Role[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { applicationId, onlyGlobal, onlyActive, search } = filters;

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await getListRolesUseCase().execute({
        page: 1,
        limit: pageSize,
        applicationId,
        onlyGlobal,
        onlyActive,
        search,
      });
      setRoles(result.data);
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setIsLoading(false);
    }
  }, [applicationId, onlyGlobal, onlyActive, search, pageSize]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = useCallback(
    async (payload: RolePayload, id?: string) => {
      setError(null);
      try {
        await getSaveRoleUseCase().execute(payload, id);
        await load();
        return true;
      } catch (cause) {
        setError(describeError(cause));
        return false;
      }
    },
    [load]
  );

  const toggleStatus = useCallback(
    async (role: Role) => {
      setError(null);
      try {
        await getToggleRoleStatusUseCase().execute(role);
        await load();
      } catch (cause) {
        setError(describeError(cause));
      }
    },
    [load]
  );

  return { roles, isLoading, error, reload: load, save, toggleStatus };
}
